/**
 * Compose the export PNG and hand it to either the Web Share API or a
 * download anchor. Mirrors the Android `ShareService` contract — the
 * source bitmap is opaque, the rects are painted at full alpha, and EXIF
 * is stripped (canvas → PNG carries no metadata by definition).
 */

import { paintExport, type RedactionRect } from "./renderer.js";

export interface ExportInputs {
  source: HTMLCanvasElement;
  imageData: ImageData;
  rects: readonly RedactionRect[];
  scalePercent: number;
}

export async function exportToBlob(inp: ExportInputs): Promise<Blob> {
  const canvas = paintExport(inp.source, inp.rects, inp.imageData, inp.scalePercent);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("PNG encode failed"))),
      "image/png",
    );
  });
}

/**
 * Try Web Share API first (mobile + a few desktop browsers); fall back to a
 * download anchor on rejection or where it's unsupported. Returns the path
 * the share actually took so the caller can decide whether to clear edits.
 */
export async function shareOrDownload(
  blob: Blob,
  filename: string,
): Promise<"shared" | "downloaded"> {
  const file = new File([blob], filename, { type: "image/png" });
  const navAny = navigator as unknown as {
    canShare?: (data: { files?: File[] }) => boolean;
    share?: (data: { files?: File[]; title?: string }) => Promise<void>;
  };
  if (navAny.share && navAny.canShare?.({ files: [file] })) {
    try {
      await navAny.share({ files: [file], title: "Redacted image" });
      return "shared";
    } catch (e) {
      // User cancelled or transient failure — fall through to download.
      const name = (e as { name?: string })?.name;
      if (name === "AbortError") return "shared";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after the click has had a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return "downloaded";
}
